import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, ChatCircleDots, Lightning } from "@phosphor-icons/react";
import { scrollToId, openBooking, EASE } from "@/lib/anim";

const LINES = ["Custom websites,", "apps & AI", "automations."];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: EASE },
  }),
};

const openChat = () => window.dispatchEvent(new Event("open-mobyle-chat"));

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 pb-16"
      data-testid="hero-section"
    >
      {/* Background video */}
      <motion.div
        style={{ y: videoY, scale: videoScale }}
        className="absolute inset-0 z-0"
      >
        <video
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster="/services/ai.jpg"
          data-testid="hero-bg-video"
        >
          <source src="/hero-bg.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* Readability + brand overlays */}
      <div className="absolute inset-0 z-[1] bg-[#050510]/55" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#050510] via-[#050510]/70 to-transparent" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#050510] via-transparent to-[#050510]/40" />
      <div className="absolute inset-0 z-[1] bg-[radial-gradient(circle_at_25%_40%,rgba(64,224,208,0.14),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(139,92,246,0.16),transparent_45%)]" />

      {/* floating color orbs */}
      <div className="absolute z-[2] top-24 left-[8%] h-3 w-3 rounded-full bg-teal shadow-[0_0_20px_6px_rgba(64,224,208,0.6)] animate-float" />
      <div className="absolute z-[2] bottom-32 left-[42%] h-2 w-2 rounded-full bg-magenta shadow-[0_0_18px_6px_rgba(236,72,153,0.6)] animate-float" style={{ animationDelay: "1.2s" }} />
      <div className="absolute z-[2] top-40 right-[12%] h-2 w-2 rounded-full bg-cobalt shadow-[0_0_18px_6px_rgba(59,110,255,0.6)] animate-float" style={{ animationDelay: "2s" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <motion.div style={{ y: textY, opacity: textOpacity }} className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2 mb-8 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5"
          >
            <Lightning size={14} weight="fill" className="text-teal" />
            <span className="text-xs uppercase tracking-[0.2em] text-white/70">
              Websites · Apps · AI Automations
            </span>
          </motion.div>

          <h1 className="font-heading font-extrabold tracking-tighter text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.2rem] leading-[0.95] drop-shadow-[0_2px_30px_rgba(0,0,0,0.6)]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.06em]">
                <motion.span
                  className="block"
                  variants={lineVariant}
                  custom={i}
                  initial="hidden"
                  animate="show"
                >
                  {i === 2 ? <span className="gradient-text">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-7 max-w-xl text-lg text-white/80 leading-relaxed"
          >
            Your business isn't a template. Stop building like it is. If you can
            describe it, we can build it — built to spec, not to a theme.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={openChat}
              className="group inline-flex items-center gap-2 font-semibold text-black rounded-full px-7 py-4 transition-transform duration-300 hover:-translate-y-0.5 bg-gradient-to-r from-teal via-cobalt to-violet shadow-[0_10px_40px_-8px_rgba(59,110,255,0.6)]"
              data-testid="hero-chat-cta"
            >
              <ChatCircleDots size={19} weight="fill" />
              What would you like to build?
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={openBooking}
              className="group inline-flex items-center gap-2 text-white border border-white/25 bg-white/5 backdrop-blur rounded-full px-7 py-4 hover:bg-white/10 transition-colors duration-300"
              data-testid="hero-book-call"
            >
              Book a call
              <ArrowRight size={17} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-10 text-xs uppercase tracking-[0.22em] text-white/50"
          >
            Free build consult · No obligation · Scoped in days, not months
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToId("#manifesto")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute z-10 bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 hover:text-teal transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.button>
    </section>
  );
};
